import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import BodyType from '../../../types/body-type/BodyType';
import BodyTypeWriteDTO from '../../../types/body-type/BodyTypeWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class BodyTypeService {

    private RESOURCE: string = 'body-type';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the body types from the backend.
     *
     * @param search The search query to filter the body types by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The body types.
     */
    public async getBodyTypes(search?: string, page: number = 0, size: number = 10): Promise<Page<BodyType>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<BodyType>>(this.RESOURCE, params);
    }

    /**
     * Fetches multiple body types from the backend.
     *
     * @param ids The IDs of the body types to fetch.
     * @return The body types.
     */
    public async getMultipleBodyTypes(ids: number[]): Promise<BodyType[]> {
        const params = new HttpParams().set('ids', ids.join(','));

        return await this.baseService.get<BodyType[]>(this.RESOURCE + '/multiple', params);
    }

    /**
     * Fetches all body types from the backend.
     *
     * @return The body types.
     */
    public async getAllBodyTypes(): Promise<BodyType[]> {
        return await this.baseService.get<BodyType[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a body type from the backend.
     *
     * @param id The ID of the body type to fetch.
     * @return The body type.
     */
    public async getBodyType(id: number): Promise<BodyType> {
        return await this.baseService.get<BodyType>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a body type in the backend.
     *
     * @param bodyType The body type to create.
     * @return Resolved when the body type is created.
     */
    public async createBodyType(bodyType: BodyTypeWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, bodyType);
    }

    /**
     * Updates a body type in the backend.
     *
     * @param bodyType The body type to update.
     * @return Resolved when the body type is updated.
     */
    public async updateBodyType(bodyType: BodyTypeWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + bodyType.id, bodyType);
    }

    /**
     * Deletes a body type in the backend.
     *
     * @param id The ID of the body type to delete.
     * @return Resolved when the body type is deleted.
     */
    public async deleteBodyType(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
